import type { WithdrawalRequest } from '../types';

export type WithdrawalRequesterKind = 'player' | 'agent' | 'shop' | 'super_agent';

type WithdrawalMeta = {
  username?: string;
  email?: string;
  agentName?: string;
  shopName?: string;
  superAgentName?: string;
};

export function getWithdrawalRequesterKind(w: WithdrawalRequest): WithdrawalRequesterKind {
  const bt = (w.buyerType || '').toLowerCase();
  if (bt === 'agent') return 'agent';
  if (bt === 'super_agent') return 'super_agent';
  if (bt === 'shop') return 'shop';
  return 'player';
}

export function getWithdrawalRequesterId(w: WithdrawalRequest): string | null {
  const kind = getWithdrawalRequesterKind(w);
  if (kind !== 'player') return w.buyerId ?? null;
  const bt = (w.buyerType || '').toLowerCase();
  if ((bt === 'user' || bt === 'player') && w.buyerId) return w.buyerId;
  return w.userId ?? w.user?.id ?? w.buyerId ?? null;
}

export function getWithdrawalRequesterPath(w: WithdrawalRequest): string | null {
  const kind = getWithdrawalRequesterKind(w);
  const id = getWithdrawalRequesterId(w);
  if (!id) return null;
  if (kind === 'agent') return `/agents/${id}`;
  if (kind === 'super_agent') return `/super-agents/${id}`;
  if (kind === 'shop') return `/shops/${id}`;
  return `/users/${id}`;
}

export function getWithdrawalRequesterTypeLabel(kind: WithdrawalRequesterKind): string {
  switch (kind) {
    case 'agent':
      return 'Agent';
    case 'super_agent':
      return 'Super agent';
    case 'shop':
      return 'Shop';
    default:
      return 'Player';
  }
}

export function getWithdrawalRequesterName(w: WithdrawalRequest): string {
  const kind = getWithdrawalRequesterKind(w);
  const meta = (w.metadata || {}) as WithdrawalMeta;
  const displayName = (w as WithdrawalRequest & { requesterDisplayName?: string }).requesterDisplayName;

  if (displayName?.trim()) return displayName.trim();

  if (kind === 'shop' && meta.shopName?.trim()) return meta.shopName.trim();
  if (kind === 'agent' && meta.agentName?.trim()) return meta.agentName.trim();
  if (kind === 'super_agent') {
    if (meta.superAgentName?.trim()) return meta.superAgentName.trim();
    if (meta.username?.trim()) return meta.username.trim();
  }

  if (kind === 'player' && w.user?.profile) {
    const fn = w.user.profile.firstName?.trim() || '';
    const ln = w.user.profile.lastName?.trim() || '';
    const full = `${fn} ${ln}`.trim();
    if (full) return full;
  }

  if (meta.username?.trim()) return meta.username.trim();

  if (w.user?.profile) {
    const fn = w.user.profile.firstName?.trim() || '';
    const ln = w.user.profile.lastName?.trim() || '';
    const full = `${fn} ${ln}`.trim();
    if (full) return full;
  }

  return '—';
}

export function getWithdrawalRequesterEmail(w: WithdrawalRequest): string | null {
  if (w.requesterEmail?.trim()) return w.requesterEmail.trim();
  const meta = (w.metadata || {}) as WithdrawalMeta;
  if (w.user?.email) return w.user.email;
  if (meta.email) return meta.email;
  return null;
}

export function getWithdrawalRequesterPhone(w: WithdrawalRequest): string | null {
  if (w.requesterPhone?.trim()) return w.requesterPhone.trim();
  if (w.user?.phone) return String(w.user.phone);
  const meta = (w.metadata || {}) as WithdrawalMeta & { phone?: string };
  if (meta.phone) return String(meta.phone);
  return null;
}

/** Short label for tables and modals */
export function getWithdrawalListLabel(w: WithdrawalRequest): string {
  const name = getWithdrawalRequesterName(w);
  if (name !== '—') return name;

  const email = getWithdrawalRequesterEmail(w);
  if (email) return email;

  const phone = getWithdrawalRequesterPhone(w);
  if (phone) return phone;

  const kind = getWithdrawalRequesterKind(w);
  const id = getWithdrawalRequesterId(w);
  if (kind !== 'player' && id) {
    return `${getWithdrawalRequesterTypeLabel(kind)} · ${id.slice(0, 8)}…`;
  }

  return id ?? '—';
}
